import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  TextField,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { TreeItem } from '@mui/x-tree-view';
import { createFolderIcon, LoadingCircFolderIcon } from './Icons'
import { FoldersGetContents, UsersEditFolderRights } from 'modules/Rest'
import { padNumbers, useIsMounted, setModes } from 'modules/Utils'
import { MessageContext } from './Contexts'
import ActionButtons from './ActionButtons'

const useStyles = makeStyles({
  noSetRights: {
    opacity: 0.5,
  },
  loading: {
    opacity: 0.5,
    marginLeft: '22px'
  },
  setRights: {
    opacity: 1.0,
  },
  formControl: {
    minWidth: 350,
  }
})

const setRightsReducer = (oldValue, newValue) => {
  const { strValue, rawValue } = { strValue: '', rawValue: null, ...newValue }
  if (strValue) {
    return strValue
  } else if (rawValue != null) {
    return JSON.stringify(rawValue)
  } else {
    return JSON.stringify(null)
  }
}

function LoadingIcon() {
  return <LoadingCircFolderIcon size={14} thickness={5}/>
}

export default function FolderTreeItem({ nodeId, userType, folder, isPlaceholder, autoload, toggledNodes, onReload: onSuperReload, rightsSelection }) {
  const [subfolders, setSubfolders] = useState([{ folder_name: 'Loading...', folder: 'placeholder-' + nodeId, isPlaceholder: true }])
  const [fetchedUserType, setFetchedUserType] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [cacheSubfolders, setCacheSubfolders] = useState(false)
  const [rightsStr, setRights] = useReducer(setRightsReducer, { rawValue: null })
  const { messageDispatch } = useContext(MessageContext)
  const isMounted = useIsMounted()

  const [modeDialog, setModeDialog] = useState('')
  // Bumped on save to remount the descendant items: their cached subtrees would otherwise keep showing the previous effective values
  const [subtreeGeneration, setSubtreeGeneration] = useState(0)

  const fetchFolders = useCallback(async () => {
    if(isMounted.current) {
      setFetching(true)
      setFetchedUserType(userType)
    }
    try {
      const { data: { folders, success, info } } = await FoldersGetContents({
        folder: nodeId,
        rights_user_type: userType,
      })
      if (success && isMounted.current) {
        const sortedFolders = [...folders]
        sortedFolders.sort((folderA, folderB) => {
          return padNumbers(folderA.folder_name) > padNumbers(folderB.folder_name) ? 1 : -1
        })
        setSubfolders(sortedFolders)
        setFetching(false)
      } else if (!success && isMounted.current) {
        throw new Error(info ? info : "An error occurred")
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [nodeId, userType, isMounted, messageDispatch])

  useEffect(() => {
    // Load subfolders if the folder has been toggled or if autoload has been set to true for the folder
    if (fetchedUserType === null && ((toggledNodes && toggledNodes.includes(nodeId)) || autoload)) {
      fetchFolders()
    }
  }, [fetchedUserType, userType, nodeId, toggledNodes, autoload, fetchFolders])

  useEffect(() => {
    // Pre-load subfolders if the folder has been toggled
    if (toggledNodes && toggledNodes.includes(nodeId)) {
      setCacheSubfolders(true)
    }
  }, [toggledNodes, nodeId])

  useEffect(() => {
    // Clear subfolders if userType is updated
    if (fetchedUserType && fetchedUserType !== userType) {
      setSubfolders([])
      setFetchedUserType(null)
    }
  }, [userType, fetchedUserType, fetchFolders])

  useEffect(() => {
    setRights({ rawValue: folder.s_rights })
  }, [folder])

  const onLabelClick = () => {
    setModeDialog('editing')
  }

  const onCancel = () => {
    setRights({ rawValue: folder.s_rights })
    setModeDialog('')
  }

  const onSave = async () => {
    try {
      setModeDialog('pending')
      const { data: { success, info } } = await UsersEditFolderRights({
        folder: nodeId,
        s_rights: JSON.parse(rightsStr),
        user_type: userType,
      })
      if (success) {
        await setModes(setModeDialog, ['completing', 1500, ''])
        onSuperReload && onSuperReload() // Trigger superfolder reload
        fetchFolders() // Trigger reload of this folder
        setSubtreeGeneration(generation => generation + 1) // Refresh the whole subtree
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeDialog('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const onRightsChange = event => {
    setRights({ strValue: event.target.value })
  }

  const styles = useStyles()

  if (isPlaceholder) {
    return (
      <TreeItem
        itemId={nodeId}
        label={<span className={styles.loading}>{folder.folder_name}</span>}
        slots={{ endIcon: LoadingIcon }}
      />
    )
  } else {
    return <>
      <Dialog open={modeDialog !== ''}>
        <DialogTitle>Edit folder rights</DialogTitle>
        <DialogContent>
          <FormControl className={styles.formControl}>
            <TextField select variant="standard" label="Rights" value={rightsStr} onChange={onRightsChange}>
              {rightsSelection.map(choice => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <ActionButtons
            mode={modeDialog}
            setMode={setModeDialog}
            action={onSave}
            cancel={onCancel}
            actionTitle='Save'
            neverShowEdit={true}
          />
        </DialogActions>
      </Dialog>

      <TreeItem
          itemId={nodeId}
          slots={{ expandIcon: fetching ? LoadingIcon: null }}
          label={
            <div onClick={onLabelClick} className={folder.s_rights ? styles.setRights : styles.noSetRights}>
              {createFolderIcon(folder.e_rights)}
              {folder.folder_name}
            </div>
          }
      >
        {subfolders.map(subfolder => (
          <FolderTreeItem
            key={`${subfolder.folder}-${subtreeGeneration}`}
            nodeId={subfolder.folder}
            userType={userType}
            folder={subfolder}
            isPlaceholder={subfolder.isPlaceholder}
            autoload={cacheSubfolders && !subfolder.isPlaceholder}
            toggledNodes={toggledNodes}
            onReload={fetchFolders}
            rightsSelection={rightsSelection}
          />
        ))}
      </TreeItem>
    </>
  }
}
